import { AspectRatio, Box, Button } from '@chakra-ui/react'
import { useRemoteUsers } from 'agora-rtc-react'
import { Close } from 'src/components/icon'
import {
  StaticScreen,
  StaticScreenTitle,
  StaticScreenContent,
} from 'src/components/StaticScreen/StaticScreen'
import { assertNever } from 'src/utils/assertNever'

import { useVideoCallRoom } from '../../context/videoCallRoomContext'
import { isScreenShareUserUid } from '../../utils/screenShare'
import { VideoCallRoomEnded } from '../VideoCallRoomEnded/VideoCallRoomEnded'
import { VideoCallRoomInCall } from '../VideoCallRoomInCall/VideoCallRoomInCall'
import { VideoCallRoomLoading } from '../VideoCallRoomLoading/VideoCallRoomLoading'
import { VideoCallRoomLocalPlayer } from '../VideoCallRoomLocalPlayer/VideoCallRoomLocalPlayer'
import { VideoCallRoomReady } from '../VideoCallRoomReady/VideoCallRoomReady'

type Props = {
  colorScheme?: 'blue' | 'green'
  endedText: string
  sendNotification?: () => void
}

export const VideoCallRoomMainContent: React.FC<Props> = props => {
  const { isForcedExitByOtherDevice } = useVideoCallRoom()
  const remoteUsers = useRemoteUsers()
  const isScreenSharing = remoteUsers.some(user =>
    isScreenShareUserUid(user.uid),
  )

  if (isForcedExitByOtherDevice) {
    return <ForcedExit />
  }
  return (
    <Box boxSize="full" pos="relative">
      <Child {...props} />
      {!isScreenSharing && (
        <AspectRatio
          ratio={16 / 9}
          pos="absolute"
          w={{ base: '192px', lg: '320px' }}
          right="8px"
          bottom="8px"
        >
          <VideoCallRoomLocalPlayer />
        </AspectRatio>
      )}
    </Box>
  )
}

const Child: React.FC<Props> = ({
  colorScheme,
  endedText,
  sendNotification,
}) => {
  const { roomStatus, connectionState } = useVideoCallRoom()

  if (roomStatus === 'ready') {
    return (
      <VideoCallRoomReady
        colorScheme={colorScheme}
        sendNotification={sendNotification}
      />
    )
  }
  if (roomStatus === 'joining') {
    return connectionState === 'CONNECTED' ? (
      <VideoCallRoomInCall colorScheme={colorScheme} />
    ) : (
      <VideoCallRoomLoading />
    )
  }

  if (roomStatus === 'left') {
    return <VideoCallRoomEnded endedText={endedText} />
  }

  return assertNever(roomStatus)
}

const ForcedExit: React.FC = () => {
  return (
    <StaticScreen>
      <StaticScreenTitle>
        他の端末でビデオ通話が開始されたため退出しました。
      </StaticScreenTitle>
      <StaticScreenContent>
        <Button
          variant="outline"
          leftIcon={<Close />}
          onClick={() => {
            window.close()
          }}
        >
          画面を閉じる
        </Button>
      </StaticScreenContent>
    </StaticScreen>
  )
}
